import React, { useContext, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContextWeb } from "../../context/AuthContextWeb";
import { API_BASE_URL } from "../../config";
import { Crosshair } from "lucide-react";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";

/* ── Constants ── */
const MAX_IMAGES = 12;
const MAX_SIZE_MB = 8;
const MAX_COMPRESSED_EDGE = 1600;
const JPEG_QUALITY = 0.78;
const DEFAULT_LAT = 27.7105;
const DEFAULT_LNG = 85.3488;

const PROPERTY_TYPES = [
  "APARTMENT",
  "HOUSE",
  "CONDO",
  "VILLA",
  "STUDIO",
  "SINGLE_ROOM",
  "COMMERCIAL_SPACE",
  "OFFICE_SPACE",
  "OTHER",
];
const FURNISHED = ["FURNISHED", "UNFURNISHED", "SEMI_FURNISHED"];
const PET_POLICY = ["ALLOWED", "NOT_ALLOWED", "NEGOTIABLE"];

const initialForm = {
  title: "",
  description: "",
  price: "",
  deposit: "",
  location: "",
  city: "",
  stateProvince: "",
  postalCode: "",
  neighborhoodInfo: "",
  propertyType: "APARTMENT",
  bedrooms: "",
  bathrooms: "",
  area: "",
  furnishedStatus: "UNFURNISHED",
  floorLevel: "",
  totalFloors: "",
  leaseDuration: "",
  maintenanceFee: "",
  amenities: "",
  petPolicy: "ALLOWED",
  securityFeatures: "",
  videoTourUrl: "",
  floorPlanUrl: "",
  energyEfficiencyRating: "",
  availableFrom: new Date().toISOString().split("T")[0],
  latitude: String(DEFAULT_LAT),
  longitude: String(DEFAULT_LNG),
};

const amenityToggles = [
  { key: "parkingAvailable", label: "Parking" },
  { key: "balcony", label: "Balcony" },
  { key: "swimmingPool", label: "Swimming Pool" },
  { key: "gym", label: "Gym" },
  { key: "heating", label: "Heating" },
  { key: "airConditioning", label: "Air Conditioning" },
];

const required = [
  "title",
  "description",
  "price",
  "location",
  "city",
  "propertyType",
];

function safeNumber(v) {
  const t = String(v).trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

function isValidUrl(u) {
  return /^https?:\/\/[^\s]+$/i.test(u.trim());
}

function renameAsJpeg(name) {
  const base = name.replace(/\.[^.]+$/, "");
  return `${base}.jpg`;
}

function calculateScaledSize(width, height, maxEdge) {
  if (width <= maxEdge && height <= maxEdge) {
    return { width, height };
  }
  const ratio = Math.min(maxEdge / width, maxEdge / height);
  return {
    width: Math.max(1, Math.round(width * ratio)),
    height: Math.max(1, Math.round(height * ratio)),
  };
}

function imageBitmapFromFile(file) {
  if (typeof createImageBitmap === "function") {
    return createImageBitmap(file);
  }

  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to decode image."));
    };
    image.src = url;
  });
}

async function optimizePhoto(file) {
  if (!file.type?.startsWith("image/")) {
    return file;
  }

  const bitmap = await imageBitmapFromFile(file);
  const scaled = calculateScaledSize(
    bitmap.width,
    bitmap.height,
    MAX_COMPRESSED_EDGE,
  );

  const canvas = document.createElement("canvas");
  canvas.width = scaled.width;
  canvas.height = scaled.height;

  const ctx = canvas.getContext("2d", { alpha: false });
  if (!ctx) {
    if (typeof bitmap.close === "function") {
      bitmap.close();
    }
    return file;
  }

  ctx.drawImage(bitmap, 0, 0, scaled.width, scaled.height);

  if (typeof bitmap.close === "function") {
    bitmap.close();
  }

  const blob = await new Promise((resolve) => {
    canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY);
  });

  if (!blob || blob.size >= file.size) {
    return file;
  }

  return new File([blob], renameAsJpeg(file.name), {
    type: "image/jpeg",
    lastModified: Date.now(),
  });
}

export default function AddPropertyPage() {
  const { user } = useContext(AuthContextWeb);
  const navigate = useNavigate();

  const [form, setForm] = useState(initialForm);
  const [boolFields, setBoolFields] = useState({
    parkingAvailable: false,
    balcony: false,
    swimmingPool: false,
    gym: false,
    heating: false,
    airConditioning: false,
  });
  const [images, setImages] = useState([]); // { file, previewUrl }
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const fileInputRef = useRef(null);
  const [isLocationLocked, setIsLocationLocked] = useState(false);
  const [locating, setLocating] = useState(false);
  const mapRef = useRef(null);
  const [mapCenter, setMapCenter] = useState({
    lat: DEFAULT_LAT,
    lng: DEFAULT_LNG,
  });

  const { isLoaded: mapsLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_KEY || "",
    libraries: [],
  });

  /* ── Helpers ── */
  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const toggleBool = (key) =>
    setBoolFields((prev) => ({ ...prev, [key]: !prev[key] }));

  /* ── Image handling ── */
  const onPickImages = async (e) => {
    const files = Array.from(e.target.files || []);
    const remaining = MAX_IMAGES - images.length;
    const picked = files.slice(0, remaining);
    const compressed = await Promise.all(
      picked.map(async (f) => {
        try {
          return await optimizePhoto(f);
        } catch {
          return f;
        }
      }),
    );

    const toAdd = compressed.filter((f) => {
      if (f.size / 1024 / 1024 > MAX_SIZE_MB) {
        return false;
      }
      return true;
    });

    const skippedCount = compressed.length - toAdd.length;
    if (skippedCount > 0) {
      alert(
        `${skippedCount} image(s) still exceed ${MAX_SIZE_MB} MB after optimization and were skipped.`,
      );
    }

    const previews = toAdd.map((file) => ({
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    setImages((prev) => [...prev, ...previews]);
    // reset so same files can be picked again if removed
    e.target.value = "";
  };

  const removeImage = (index) => {
    setImages((prev) => {
      URL.revokeObjectURL(prev[index].previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  };

  /* ── Map / location ── */
  const handleMyLocation = () => {
    if (locating || isLocationLocked) return;
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser.");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        set("latitude", String(lat));
        set("longitude", String(lng));
        setMapCenter({ lat, lng });
        mapRef.current?.panTo({ lat, lng });
        setLocating(false);
      },
      () => {
        alert(
          "Could not get your location. Please allow location access and try again.",
        );
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const toggleLocationLock = () => {
    if (!isLocationLocked) {
      setIsLocationLocked(true);
      setErrors((prev) => {
        const { latitude, longitude, ...rest } = prev;
        return rest;
      });
    } else {
      setIsLocationLocked(false);
    }
  };

  /* ── Validation ── */
  const validate = () => {
    const errs = {};
    required.forEach((k) => {
      if (!form[k]?.trim()) errs[k] = "Required";
    });
    if (form.videoTourUrl.trim() && !isValidUrl(form.videoTourUrl))
      errs.videoTourUrl = "Invalid URL";
    if (form.floorPlanUrl.trim() && !isValidUrl(form.floorPlanUrl))
      errs.floorPlanUrl = "Invalid URL";
    if (!isLocationLocked)
      errs.latitude = "Please confirm your location on the map";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  /* ── Submit ── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!validate()) return;

    setSubmitting(true);
    setSubmitError("");

    const payload = {
      ownerId: Number(user.userId),
      title: form.title.trim(),
      description: form.description.trim(),
      price: safeNumber(form.price),
      deposit: safeNumber(form.deposit),
      location: form.location.trim(),
      latitude: Number(form.latitude),
      longitude: Number(form.longitude),
      city: form.city.trim(),
      stateProvince: form.stateProvince.trim(),
      postalCode: form.postalCode.trim(),
      neighborhoodInfo: form.neighborhoodInfo.trim(),
      propertyType: form.propertyType,
      bedrooms: safeNumber(form.bedrooms),
      bathrooms: safeNumber(form.bathrooms),
      area: safeNumber(form.area),
      furnishedStatus: form.furnishedStatus,
      floorLevel: safeNumber(form.floorLevel),
      totalFloors: safeNumber(form.totalFloors),
      availableFrom: form.availableFrom,
      leaseDuration: safeNumber(form.leaseDuration),
      maintenanceFee: safeNumber(form.maintenanceFee),
      amenities: form.amenities.trim(),
      petPolicy: form.petPolicy,
      securityFeatures: form.securityFeatures.trim(),
      videoTourUrl: form.videoTourUrl.trim(),
      floorPlanUrl: form.floorPlanUrl.trim(),
      energyEfficiencyRating: form.energyEfficiencyRating.trim(),
      ...boolFields,
    };

    const formData = new FormData();
    formData.append("property", JSON.stringify(payload));
    images.forEach(({ file }) => formData.append("images", file));

    try {
      const token = localStorage.getItem("rh_access_token");
      const res = await fetch(`${API_BASE_URL}/properties`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!res.ok) {
        const errBody = await res.text();
        let msg;
        try {
          msg = JSON.parse(errBody)?.message || errBody;
        } catch {
          msg = errBody || `Server error ${res.status}`;
        }
        throw new Error(msg);
      }

      navigate("/owner/listings", {
        state: { toast: "Property listed successfully!" },
      });
    } catch (err) {
      setSubmitError(err?.message || "Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Field helpers ── */
  const F = ({ label, name, type = "text", placeholder, required: req }) => (
    <div className={`prop-field${errors[name] ? " prop-field--error" : ""}`}>
      <label className="prop-label">
        {label}
        {req && <span className="prop-required"> *</span>}
      </label>
      <input
        className="prop-input"
        type={type}
        name={name}
        value={form[name]}
        onChange={(e) => set(name, e.target.value)}
        placeholder={placeholder || ""}
      />
      {errors[name] && <p className="prop-field-error">{errors[name]}</p>}
    </div>
  );

  const T = ({ label, name, placeholder, rows = 4 }) => (
    <div className={`prop-field${errors[name] ? " prop-field--error" : ""}`}>
      <label className="prop-label">{label}</label>
      <textarea
        className="prop-input prop-textarea"
        name={name}
        rows={rows}
        value={form[name]}
        onChange={(e) => set(name, e.target.value)}
        placeholder={placeholder || ""}
      />
      {errors[name] && <p className="prop-field-error">{errors[name]}</p>}
    </div>
  );

  const S = ({ label, name, options }) => (
    <div className="prop-field">
      <label className="prop-label">{label}</label>
      <select
        className="prop-input prop-select"
        value={form[name]}
        onChange={(e) => set(name, e.target.value)}
      >
        {options.map((o) => (
          <option key={o} value={o}>
            {o.replace(/_/g, " ")}
          </option>
        ))}
      </select>
    </div>
  );

  return (
    <div className="owner-page">
      <div className="owner-page-header">
        <h1 className="owner-page-title">Add New Property</h1>
        <p className="owner-page-subtitle">
          Fill in the details below to list your property.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="prop-form" noValidate>
        {/* ── Basic Info ── */}
        <fieldset className="prop-section">
          <legend className="prop-section-title">Basic Information</legend>
          <div className="prop-grid-2">
            <F
              label="Title"
              name="title"
              required
              placeholder="e.g. Cozy 2BHK in Lalitpur"
            />
            <S
              label="Property Type"
              name="propertyType"
              options={PROPERTY_TYPES}
            />
          </div>
          <T
            label="Description"
            name="description"
            required
            placeholder="Describe the property…"
          />
        </fieldset>

        {/* ── Pricing ── */}
        <fieldset className="prop-section">
          <legend className="prop-section-title">Pricing</legend>
          <div className="prop-grid-3">
            <F
              label="Monthly Rent (NPR)"
              name="price"
              type="number"
              required
              placeholder="15000"
            />
            <F
              label="Security Deposit (NPR)"
              name="deposit"
              type="number"
              placeholder="30000"
            />
            <F
              label="Maintenance Fee (NPR)"
              name="maintenanceFee"
              type="number"
              placeholder="500"
            />
          </div>
        </fieldset>

        {/* ── Location ── */}
        <fieldset className="prop-section">
          <legend className="prop-section-title">Location</legend>
          <F
            label="Full Address / Location"
            name="location"
            required
            placeholder="Kumaripati, Lalitpur"
          />
          <div className="prop-grid-2">
            <F label="City" name="city" required placeholder="Lalitpur" />
            <F
              label="State / Province"
              name="stateProvince"
              placeholder="Bagmati"
            />
            <F label="Postal Code" name="postalCode" placeholder="44600" />
            <F
              label="Neighborhood Info"
              name="neighborhoodInfo"
              placeholder="Near City Centre"
            />
          </div>
          {/* Map header: label + "Use My Location" */}
          <div className="prop-map-header">
            <span className="prop-label">Pin Your Property Location</span>
            <button
              type="button"
              className="prop-map-locate-btn"
              onClick={handleMyLocation}
              disabled={locating || isLocationLocked}
            >
              <Crosshair size={14} strokeWidth={2} />
              {locating ? "Locating\u2026" : "Use My Location"}
            </button>
          </div>

          {/* Interactive map */}
          <div
            className={`prop-map-wrap${
              isLocationLocked ? " prop-map-wrap--locked" : ""
            }`}
          >
            {!mapsLoaded ? (
              <div className="prop-map-loading">Loading map…</div>
            ) : (
              <GoogleMap
                mapContainerClassName="prop-map"
                center={mapCenter}
                zoom={15}
                onClick={(e) => {
                  if (!isLocationLocked) {
                    const lat = e.latLng.lat();
                    const lng = e.latLng.lng();
                    set("latitude", String(lat));
                    set("longitude", String(lng));
                  }
                }}
                options={{
                  streetViewControl: false,
                  mapTypeControl: false,
                  fullscreenControl: false,
                  gestureHandling: "greedy",
                }}
                onLoad={(map) => {
                  mapRef.current = map;
                }}
              >
                <Marker
                  position={{
                    lat: Number(form.latitude),
                    lng: Number(form.longitude),
                  }}
                  draggable={!isLocationLocked}
                  onDragEnd={(e) => {
                    set("latitude", String(e.latLng.lat()));
                    set("longitude", String(e.latLng.lng()));
                  }}
                />
              </GoogleMap>
            )}

            {/* Coordinates badge */}
            <div className="prop-map-coords">
              {Number(form.latitude).toFixed(5)},{" "}
              {Number(form.longitude).toFixed(5)}
            </div>

            {/* Floating confirm / edit button */}
            <button
              type="button"
              className={`prop-map-confirm-btn${
                isLocationLocked ? " prop-map-confirm-btn--locked" : ""
              }`}
              onClick={toggleLocationLock}
            >
              {isLocationLocked ? "Edit Location" : "Confirm Location"}
            </button>
          </div>

          {errors.latitude && (
            <p className="prop-field-error" style={{ marginTop: "0.4rem" }}>
              {errors.latitude}
            </p>
          )}
        </fieldset>

        {/* ── Property Details ── */}
        <fieldset className="prop-section">
          <legend className="prop-section-title">Property Details</legend>
          <div className="prop-grid-3">
            <F label="Bedrooms" name="bedrooms" type="number" placeholder="2" />
            <F
              label="Bathrooms"
              name="bathrooms"
              type="number"
              placeholder="1"
            />
            <F
              label="Area (sq ft)"
              name="area"
              type="number"
              placeholder="800"
            />
          </div>
          <div className="prop-grid-3">
            <F
              label="Floor Level"
              name="floorLevel"
              type="number"
              placeholder="2"
            />
            <F
              label="Total Floors"
              name="totalFloors"
              type="number"
              placeholder="4"
            />
            <F
              label="Lease Duration (months)"
              name="leaseDuration"
              type="number"
              placeholder="12"
            />
          </div>
          <div className="prop-grid-3">
            <S
              label="Furnished Status"
              name="furnishedStatus"
              options={FURNISHED}
            />
            <S label="Pet Policy" name="petPolicy" options={PET_POLICY} />
            <F label="Available From" name="availableFrom" type="date" />
          </div>
        </fieldset>

        {/* ── Amenities ── */}
        <fieldset className="prop-section">
          <legend className="prop-section-title">Amenities</legend>
          <div className="prop-toggles">
            {amenityToggles.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                className={`prop-toggle${boolFields[key] ? " prop-toggle--on" : ""}`}
                onClick={() => toggleBool(key)}
              >
                {boolFields[key] ? "✓ " : ""}
                {label}
              </button>
            ))}
          </div>
          <T
            label="Other Amenities"
            name="amenities"
            rows={2}
            placeholder="e.g. 24/7 security, backup power…"
          />
          <div className="prop-grid-2">
            <F
              label="Security Features"
              name="securityFeatures"
              placeholder="CCTV, gated compound…"
            />
            <F
              label="Energy Efficiency Rating"
              name="energyEfficiencyRating"
              placeholder="A, B, C…"
            />
          </div>
        </fieldset>

        {/* ── Media ── */}
        <fieldset className="prop-section">
          <legend className="prop-section-title">Media</legend>
          <div className="prop-grid-2">
            <div
              className={`prop-field${errors.videoTourUrl ? " prop-field--error" : ""}`}
            >
              <label className="prop-label">Video Tour URL</label>
              <input
                className="prop-input"
                type="url"
                value={form.videoTourUrl}
                onChange={(e) => set("videoTourUrl", e.target.value)}
                placeholder="https://youtube.com/…"
              />
              {errors.videoTourUrl && (
                <p className="prop-field-error">{errors.videoTourUrl}</p>
              )}
            </div>
            <div
              className={`prop-field${errors.floorPlanUrl ? " prop-field--error" : ""}`}
            >
              <label className="prop-label">Floor Plan URL</label>
              <input
                className="prop-input"
                type="url"
                value={form.floorPlanUrl}
                onChange={(e) => set("floorPlanUrl", e.target.value)}
                placeholder="https://…"
              />
              {errors.floorPlanUrl && (
                <p className="prop-field-error">{errors.floorPlanUrl}</p>
              )}
            </div>
          </div>

          {/* Image upload */}
          <div className="prop-field">
            <label className="prop-label">
              Property Photos{" "}
              <span className="prop-hint-inline">
                (up to {MAX_IMAGES}, max {MAX_SIZE_MB}MB each)
              </span>
            </label>
            <div className="prop-image-grid">
              {images.map(({ previewUrl }, idx) => (
                <div key={idx} className="prop-img-thumb">
                  <img src={previewUrl} alt={`preview ${idx + 1}`} />
                  <button
                    type="button"
                    className="prop-img-remove"
                    onClick={() => removeImage(idx)}
                    aria-label="Remove image"
                  >
                    ✕
                  </button>
                </div>
              ))}
              {images.length < MAX_IMAGES && (
                <button
                  type="button"
                  className="prop-img-add"
                  onClick={() => fileInputRef.current?.click()}
                >
                  + Add Photo
                </button>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              style={{ display: "none" }}
              onChange={onPickImages}
            />
          </div>
        </fieldset>

        {/* ── Submit ── */}
        {submitError && (
          <p className="form-error prop-submit-error">{submitError}</p>
        )}

        <div className="prop-submit-row">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate("/owner/listings")}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary prop-submit-btn"
            disabled={submitting}
          >
            {submitting ? "Submitting…" : "List Property"}
          </button>
        </div>
      </form>
    </div>
  );
}
